import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ExternalLink, Loader2, PanelLeftClose, PanelLeftOpen, Send, Sparkles } from 'lucide-react';
import { AiBadge } from '@/components/atoms/ai-badge';
import { DetailPageLayout } from '@/components/layouts';
import { OttoMessageText } from '@/components/ai/otto/OttoMessageText';
import { OttoReportView } from '@/components/ai/otto/OttoReportView';
import { OttoAskBar } from '@/components/ai/otto/OttoAskBar';
import { SuggestionChips } from '@/components/ai/otto/SuggestionChips';
import { Fullbleed } from '@/contexts/fullbleed';
import {
  fetchOttoReport,
  useAskOtto,
  useOttoReports,
  useOttoStatus,
  useOttoSuggestions,
  type OttoHistoryMessage,
  type OttoReport,
} from '@/lib/ai/otto';

export const Route = createFileRoute('/_dashboard/dashboard/ai/otto')({
  component: OttoPage,
});

type Turn =
  | { id: string; role: 'user'; kind: 'text'; text: string }
  | { id: string; role: 'otto'; kind: 'text'; text: string }
  | {
      id: string;
      role: 'otto';
      kind: 'report';
      report: OttoReport;
      followUps?: string[];
    };

let turnSeq = 0;
const nextId = () => `t${++turnSeq}`;

// Keep each history message well under the backend's 8000-char per-message cap
// so a text-heavy report summary can't 400 the next /ask.
const MAX_HISTORY_CHARS = 6000;
const cap = (s: string) => (s.length > MAX_HISTORY_CHARS ? `${s.slice(0, MAX_HISTORY_CHARS)}…` : s);

// AskOttoDto's limits on a question: shorter or longer only comes back as a 400.
const QUESTION_MIN_LENGTH = 3;
const QUESTION_MAX_LENGTH = 1000;

// Serialize the transcript into history Otto can reason over for follow-ups.
// Report turns become a compact summary (question + SQL + a small row sample)
// so "list all of them" resolves against the prior query. Capped to recent turns.
function toHistory(turns: Turn[]): OttoHistoryMessage[] {
  return turns.slice(-12).map((turn) => {
    if (turn.role === 'user') return { role: 'user', content: cap(turn.text) };
    if (turn.kind === 'text') return { role: 'otto', content: cap(turn.text) };
    const r = turn.report;
    const sample = JSON.stringify((r.rows ?? []).slice(0, 3));
    return {
      role: 'otto',
      content: cap(
        `Answered "${r.question}" by running SQL:\n${r.sql}\n` +
          `Columns: ${r.columns.map((c) => c.key).join(', ')}. ` +
          `${r.rowCount} rows. Sample: ${sample}`,
      ),
    };
  });
}

// Read the persisted sidebar state, tolerating environments where localStorage
// access throws (privacy modes, sandboxed iframes). Defaults to open.
function readSidebarPref(): boolean {
  try {
    return globalThis.localStorage?.getItem('otto:sidebar') !== 'closed';
  } catch {
    return true;
  }
}

function OttoPage() {
  const status = useOttoStatus();
  const ready = (status.data?.ready ?? status.data?.enabled) === true;
  const suggestions = useOttoSuggestions(ready);
  const reports = useOttoReports();
  const ask = useAskOtto();

  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  // Recent-reports sidebar collapse, remembered across visits. Storage access
  // can throw (disabled/sandboxed), so it's guarded — default to open.
  const [sidebarOpen, setSidebarOpen] = useState(() => readSidebarPref());

  const transcriptRef = useRef<HTMLDivElement>(null);

  function toggleSidebar() {
    setSidebarOpen((open) => {
      const next = !open;
      try {
        globalThis.localStorage?.setItem('otto:sidebar', next ? 'open' : 'closed');
      } catch {
        // Ignore: persistence is best-effort, the toggle still works this session.
      }
      return next;
    });
  }

  useEffect(() => {
    // Scroll the transcript container itself (not scrollIntoView, which would
    // also scroll ancestor scroll containers and can jank the whole page).
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, ask.isPending]);

  function submit(q: string) {
    const text = q.trim();
    if (!text || ask.isPending) return;
    const history = toHistory(turns);
    setTurns((prev) => [...prev, { id: nextId(), role: 'user', kind: 'text', text }]);
    setQuestion('');
    ask.mutate(
      { question: text, history },
      {
        onSuccess: (res) => {
          setTurns((prev) => [
            ...prev,
            res.kind === 'answer'
              ? { id: nextId(), role: 'otto', kind: 'text', text: res.text }
              : {
                  id: nextId(),
                  role: 'otto',
                  kind: 'report',
                  report: res.report,
                  followUps: res.followUps,
                },
          ]);
        },
        onError: () =>
          setTurns((prev) => [
            ...prev,
            {
              id: nextId(),
              role: 'otto',
              kind: 'text',
              text: 'Sorry — something went wrong answering that. Please try again.',
            },
          ]),
      },
    );
  }

  // Load a saved report inline (into the transcript) instead of a new tab.
  // Always appends (even if already shown) so a click brings it back into view
  // with feedback, rather than silently doing nothing.
  async function loadReport(id: string) {
    try {
      const report = await fetchOttoReport(id);
      setTurns((prev) => [...prev, { id: nextId(), role: 'otto', kind: 'report', report }]);
    } catch {
      setTurns((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'otto',
          kind: 'text',
          text: 'Could not load that report.',
        },
      ]);
    }
  }

  // `ready` is configured AND reachable. Split the two so an operator sees
  // "nobody set this up" and "it is set up but the database won't answer" as
  // the different problems they are. Older servers/cached responses only have `enabled`.
  const disabled = status.data ? !(status.data.ready ?? status.data.enabled) : false;
  const unreachable = status.data?.enabled === true && status.data.database?.reachable === false;
  const showWelcome = turns.length === 0;

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        // Own the scrolling ourselves: make the content a single bounded,
        // non-scrolling flex box so the transcript is the ONLY scroll container
        // (avoids nested scrollers + the h-full chain collapsing after many
        // reports, which floated the input up and hid the sidebar).
        contentClassName="flex min-h-0 overflow-y-hidden p-0"
        header={
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="size-4 shrink-0 text-primary" />
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Otto</h1>
              <p className="text-xs text-muted-foreground">
                Chat with your data — ask a question or ask Otto what it can do.
              </p>
            </div>
            <AiBadge className="ml-1" />
          </div>
        }
      >
        {disabled ? (
          <div className="flex flex-1 items-center justify-center p-4">
            <div className="max-w-md rounded-lg border border-dashed p-8 text-center">
              <Sparkles className="mx-auto mb-3 size-6 text-muted-foreground" />
              <h2 className="text-sm font-semibold">
                {unreachable ? "Otto can't reach its database" : "Otto isn't set up yet"}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {unreachable
                  ? "Otto is configured, but its read-only database isn't answering. An administrator can check the connection details."
                  : "Ask an administrator to provision Otto's read-only database access for this environment."}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 gap-4 p-4">
            {/* History sidebar — collapsible (state remembered) */}
            {sidebarOpen ? (
              <aside className="hidden w-64 shrink-0 flex-col overflow-hidden border-r pr-3 md:flex">
                <div className="flex items-center justify-between px-1 pb-1">
                  <p className="text-xs font-medium text-muted-foreground">Recent reports</p>
                  <button
                    type="button"
                    onClick={toggleSidebar}
                    title="Collapse panel"
                    aria-label="Collapse recent reports"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <PanelLeftClose className="size-4" />
                  </button>
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                  {reports.data?.length ? (
                    reports.data.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => loadReport(r.id)}
                        className="rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted"
                      >
                        <span className="line-clamp-2">{r.question}</span>
                        <span className="mt-0.5 block text-[10px] text-muted-foreground">
                          {r.status === 'failed' ? 'failed' : `${r.rowCount.toLocaleString()} rows`}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="px-1 text-xs text-muted-foreground">No reports yet.</p>
                  )}
                </div>
              </aside>
            ) : (
              <button
                type="button"
                onClick={toggleSidebar}
                title="Show recent reports"
                aria-label="Show recent reports"
                className="hidden h-fit shrink-0 rounded-md border p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground md:block"
              >
                <PanelLeftOpen className="size-4" />
              </button>
            )}

            {/* Chat column */}
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              {/* Transcript */}
              <div ref={transcriptRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                {/* Welcome / intro */}
                {showWelcome && (
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                      <Sparkles className="size-4 text-primary" />
                      Otto
                    </div>
                    {suggestions.isLoading ? (
                      <p className="text-sm text-muted-foreground">
                        <Loader2 className="mr-2 inline size-3.5 animate-spin" />
                        Getting to know your data…
                      </p>
                    ) : (
                      <>
                        <OttoMessageText
                          text={
                            suggestions.data?.intro ??
                            "Hi, I'm Otto. Ask me anything about your business data and I'll build a report — or ask me what I can do."
                          }
                        />
                        {!!suggestions.data?.examples?.length && (
                          <div className="mt-3">
                            <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                              Try asking:
                            </p>
                            <SuggestionChips
                              suggestions={suggestions.data.examples}
                              onPick={submit}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {turns.map((turn) =>
                  turn.role === 'user' ? (
                    <div key={turn.id} className="flex justify-end">
                      <div className="max-w-[85%] rounded-lg bg-primary/10 px-3 py-2 text-sm">
                        {turn.text}
                      </div>
                    </div>
                  ) : turn.kind === 'text' ? (
                    <div key={turn.id} className="flex gap-2">
                      <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <OttoMessageText text={turn.text} />
                      </div>
                    </div>
                  ) : (
                    <div key={turn.id} className="flex gap-2">
                      <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="rounded-lg border p-3">
                          <OttoReportView report={turn.report} />
                          {turn.report.status !== 'failed' && (
                            <Link
                              to="/dashboard/ai/otto/$reportId"
                              params={{ reportId: turn.report.id }}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              Open report in new tab
                              <ExternalLink className="size-3" />
                            </Link>
                          )}
                        </div>
                        {/* Suggested follow-ups */}
                        {!!turn.followUps?.length && (
                          <SuggestionChips
                            suggestions={turn.followUps}
                            onPick={submit}
                            disabled={ask.isPending}
                          />
                        )}
                      </div>
                    </div>
                  ),
                )}

                {ask.isPending && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    Otto is thinking…
                  </div>
                )}
              </div>

              <OttoAskBar
                value={question}
                onChange={setQuestion}
                onSubmit={submit}
                pending={ask.isPending}
                label="Ask Otto"
                placeholder="Ask about your data, or ask “what can you do?”…"
                submitLabel="Ask"
                submitIcon={Send}
                minLength={QUESTION_MIN_LENGTH}
                maxLength={QUESTION_MAX_LENGTH}
              />
            </div>
          </div>
        )}
      </DetailPageLayout>
    </>
  );
}
